import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wine, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Welcome to Mixed Up!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card animate-fadeIn">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Wine className="w-8 h-8 text-[#D4AF37]" />
            <span className="font-playfair text-2xl font-bold text-[#F5F5F5]">Mixed Up</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#F5F5F5] mb-2">Create Account</h1>
          <p className="text-[#A3A3A3]">Start discovering cocktails today</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#A3A3A3]">Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="pl-10 bg-black/20 border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252]"
                required
                data-testid="register-name-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#A3A3A3]">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 bg-black/20 border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252]"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#A3A3A3]">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 bg-black/20 border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252]"
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full py-6 gold-glow btn-press"
            data-testid="register-submit-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Create Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
        </form>

        <p className="text-center mt-6 text-[#A3A3A3]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#D4AF37] hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
